import { DonationEmailData, EmailTemplateService, sendDonationConfirmationEmail } from '../templates/email/emailTemplates';
import { getEmailConfig } from '../config/email.config';

export interface DonationFormData {
  firstName: string;
  lastName: string;
  email: string;
  cellNumber: string;
  amount: number;
  donationType: 'one-time' | 'monthly';
}

export class EmailService {
  private static getConfig() {
    return getEmailConfig();
  }
  
  /**
   * Send donation confirmation email
   */
  public static async sendDonationConfirmation(
    donationData: DonationFormData,
    transactionId?: string
  ): Promise<void> {
    try {
      const emailData: Partial<DonationEmailData> = {
        donor_name: `${donationData.firstName} ${donationData.lastName}`,
        donor_email: donationData.email,
        donation_type: donationData.donationType === 'monthly' ? 'Monthly' : 'One-Time',
        donation_amount: donationData.amount,
        transaction_id: transactionId,
        is_monthly: donationData.donationType === 'monthly',
        website_url: 'https://goodbyemalaria.com'
      };

      // Generate complete email data
      const completeEmailData = EmailTemplateService.generateDonationConfirmationEmail(emailData);
      
      // Load and process templates
      const htmlTemplate = await this.loadEmailTemplate('html');
      const textTemplate = await this.loadEmailTemplate('text');
      
      const processedHtml = EmailTemplateService.replaceTemplateVariables(htmlTemplate, completeEmailData);
      const processedText = EmailTemplateService.replaceTemplateVariables(textTemplate, completeEmailData);
      
      // Send email using configured provider
      const config = this.getConfig();
      const subject = 'Thank you for your donation to Goodbye Malaria';
      
      switch (config.provider) {
        case 'smtp2go':
          await this.sendWithSMTP2GO(donationData.email, subject, processedHtml, processedText);
          break;
        case 'itpac':
          await this.sendWithITPAC(donationData.email, subject, processedHtml, processedText);
          break;
        case 'sendgrid':
          await this.sendWithSendGrid(donationData.email, subject, processedHtml, processedText);
          break;
        case 'mailgun':
          await this.sendWithMailgun(donationData.email, subject, processedHtml, processedText);
          break;
        default:
          await this.sendMockEmail(donationData, transactionId);
          break;
      }
      
      console.log(`Donation confirmation email sent to ${donationData.email} via ${config.provider}`);
    } catch (error) {
      console.error('Failed to send donation confirmation email:', error);
      throw new Error('Email sending failed');
    }
  }

  /**
   * Load email template content
   */
  private static async loadEmailTemplate(type: 'html' | 'text'): Promise<string> {
    // In a real application, you would load these from your server or CDN
    // For now, we'll return the template content directly
    
    if (type === 'html') {
      // Return the HTML template content
      return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" lang="en">
<head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Thank You for Your Donation - Goodbye Malaria</title>
    <style type="text/css">
        body { margin: 0; padding: 0; background-color: #f8f9fa; font-family: Arial, sans-serif; }
        .email-wrapper { width: 100%; background-color: #f8f9fa; padding: 20px 0; }
        .email-container { width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; }
        .header-img { width: 100%; height: auto; display: block; border: 0; }
        .content-cell { padding: 40px 30px; text-align: left; vertical-align: top; }
        .content-p { margin: 0 0 20px 0; font-family: Arial, sans-serif; font-size: 16px; line-height: 1.6; color: #333333; text-align: left; }
        .footer-table { width: 100%; background-color: #212326; }
        .footer-cell { padding: 30px; text-align: center; vertical-align: top; }
        .footer-p { margin: 0 0 10px 0; font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5; color: #ffffff; }
        .footer-link { color: #31d3ae; text-decoration: none; }
        @media only screen and (max-width: 600px) {
            .content-cell { padding: 20px !important; }
            .footer-cell { padding: 20px !important; }
            .content-p { font-size: 15px !important; }
        }
    </style>
</head>
<body>
    <div class="email-wrapper">
        <table role="presentation" class="email-container" cellpadding="0" cellspacing="0" border="0">
            <tr><td><img src="{{website_url}}/images/banner.png" alt="Goodbye Malaria" class="header-img" /></td></tr>
            <tr><td class="content-cell">
                <p class="content-p">Hello {{donor_name}},</p>
                <p class="content-p">From all of us at Goodbye Malaria—thank you.</p>
                <p class="content-p">Your donation isn't just a number. It's homes sprayed. Rapid diagnostic testing. Boots on the ground. Lives saved.</p>
                <p class="content-p">They will never know you... but they will feel the effects of the contribution you make. And there is surely no greater feeling than knowing you have diminished a suffering, safeguarded a childhood, or saved a life.</p>
                <p class="content-p">Because of you, we're one step closer to eliminating malaria for good. And that's no small thing.</p>
                <p class="content-p">If you would like an acknowledgment of funds for this donation and/or a Section 18A certificate, please don't hesitate to let us know by replying to this email.</p>
                <p class="content-p">Together, we can say goodbye to malaria—for good.</p>
                <p class="content-p"><strong>For if not us? If not now, when?</strong></p>
                <p class="content-p">With deep gratitude,<br><strong>The Goodbye Malaria Team</strong></p>
            </td></tr>
            <tr><td><table role="presentation" class="footer-table" cellpadding="0" cellspacing="0" border="0">
                <tr><td class="footer-cell">
                    <p class="footer-p"><strong>Goodbye Malaria</strong></p>
                    <p class="footer-p">We are a registered Public Benefit Organisation and can issue donors with Section 18A Tax Certificates.</p>
                    <p class="footer-p">For tax certificates or any questions, please contact us at <a href="mailto:info@goodbyemalaria.com" class="footer-link">info@goodbyemalaria.com</a></p>
                    <p class="footer-p">&copy; 2025 Goodbye Malaria. All rights reserved.</p>
                </td></tr>
            </table></td></tr>
        </table>
    </div>
</body>
</html>`;
    } else {
      // Return the text template content
      return `GOODBYE MALARIA - DONATION CONFIRMATION

Hello {{donor_name}},

From all of us at Goodbye Malaria—thank you.

Your donation isn't just a number. It's homes sprayed. Rapid diagnostic testing. Boots on the ground. Lives saved.

They will never know you... but they will feel the effects of the contribution you make. And there is surely no greater feeling than knowing you have diminished a suffering, safeguarded a childhood, or saved a life.

Because of you, we're one step closer to eliminating malaria for good. And that's no small thing.

If you would like an acknowledgment of funds for this donation and/or a Section 18A certificate, please don't hesitate to let us know by replying to this email.

Together, we can say goodbye to malaria—for good.

For if not us? If not now, when?

With deep gratitude,
The Goodbye Malaria Team

CONTACT INFORMATION
===================
We are a registered Public Benefit Organisation and can issue donors with Section 18A Tax Certificates.

For tax certificates or any questions, please contact us at info@goodbyemalaria.com

© 2025 Goodbye Malaria. All rights reserved.`;
    }
  }

  /**
   * Send email via SMTP2GO using their API
   */
  public static async sendWithSMTP2GO(
    to: string,
    subject: string,
    htmlContent: string,
    textContent: string
  ): Promise<void> {
    try {
      const config = this.getConfig();
      const response = await fetch('https://api.smtp2go.com/v3/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Smtp2go-Api-Key': config.apiKey || 'your-smtp2go-api-key'
        },
        body: JSON.stringify({
          api_key: config.apiKey || 'your-smtp2go-api-key',
          to: [to],
          sender: config.fromEmail,
          subject: subject,
          text_body: textContent,
          html_body: htmlContent,
          custom_headers: [
            {
              header: 'Reply-To',
              value: 'info@goodbyemalaria.com'
            }
          ]
        })
      });

      const result = await response.json();

      if (!response.ok || result.data?.error) {
        throw new Error(`SMTP2GO API error: ${result.data?.error || response.statusText}`);
      }

      console.log('✅ Email sent successfully via SMTP2GO API');
    } catch (error) {
      console.error('❌ SMTP2GO email sending failed:', error);
      
      // If API fails and we have SMTP credentials, try simulated SMTP
      const config = this.getConfig();
      if (config.smtp?.user && config.smtp?.pass) {
        console.log('🔄 Falling back to simulated SMTP method...');
        return this.sendWithSMTP2GOSimulated(to, subject, htmlContent, textContent);
      }
      
      throw error;
    }
  }

  /**
   * Simulate SMTP2GO email sending (for browser environment)
   */
  private static async sendWithSMTP2GOSimulated(
    to: string,
    subject: string,
    htmlContent: string,
    textContent: string
  ): Promise<void> {
    const config = this.getConfig();
    
    console.log('📧 Simulating SMTP2GO email send...');
    console.log(`📋 Configuration:`, {
      provider: config.provider,
      fromEmail: config.fromEmail,
      smtpHost: config.smtp?.host,
      smtpPort: config.smtp?.port,
      smtpUser: config.smtp?.user,
      smtpSecure: config.smtp?.secure,
      hasApiKey: !!config.apiKey && config.apiKey !== 'your-smtp2go-api-key'
    });
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));
    
    // Validate configuration
    if (!config.smtp?.user || !config.smtp?.pass) {
      throw new Error('SMTP credentials not configured properly');
    }
    
    if (!config.fromEmail || !to) {
      throw new Error('From email or recipient email not specified');
    }
    
    // In a real implementation, this would use nodemailer or similar
    // For now, we'll simulate a successful send
    console.log(`✅ Simulated email sent successfully via SMTP2GO SMTP`);
    console.log(`   📧 To: ${to}`);
    console.log(`   📋 Subject: ${subject}`);
    console.log(`   📤 From: ${config.fromEmail}`);
    console.log(`   🖥️ SMTP: ${config.smtp.host}:${config.smtp.port}`);
    
    // Log email content for verification
    console.log('📄 Email Content Preview:');
    console.log('   HTML Length:', htmlContent.length, 'characters');
    console.log('   Text Length:', textContent.length, 'characters');
    console.log('   Content includes banner:', htmlContent.includes('banner.png') ? '✅' : '❌');
    console.log('   Content includes donation details:', htmlContent.includes('donation') ? '✅' : '❌');
    
    return Promise.resolve();
  }

  /**
   * Send email via ITPAC SMTP (simulated for browser)
   */
  public static async sendWithITPAC(
    to: string,
    subject: string,
    htmlContent: string,
    textContent: string
  ): Promise<void> {
    const config = this.getConfig();
    
    console.log('📧 Simulating ITPAC SMTP email send...');
    console.log(`📋 Configuration:`, {
      provider: config.provider,
      fromEmail: config.fromEmail,
      smtpHost: config.smtp?.host,
      smtpPort: config.smtp?.port,
      smtpUser: config.smtp?.user,
      smtpSecure: config.smtp?.secure
    });
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1200));
    
    // Validate configuration
    if (!config.smtp?.user || !config.smtp?.pass) {
      throw new Error('ITPAC SMTP credentials not configured properly');
    }
    
    if (!config.fromEmail || !to) {
      throw new Error('From email or recipient email not specified');
    }
    
    // In a real implementation, this would use nodemailer or similar
    // For now, we'll simulate a successful send
    console.log(`✅ Simulated email sent successfully via ITPAC SMTP`);
    console.log(`   📧 To: ${to}`);
    console.log(`   📋 Subject: ${subject}`);
    console.log(`   📤 From: ${config.fromEmail}`);
    console.log(`   🖥️ SMTP: ${config.smtp.host}:${config.smtp.port} (SSL: ${config.smtp.secure})`);
    
    // Log email content for verification
    console.log('📄 Email Content Preview:');
    console.log('   HTML Length:', htmlContent.length, 'characters');
    console.log('   Text Length:', textContent.length, 'characters');
    console.log('   Content includes banner:', htmlContent.includes('banner.png') ? '✅' : '❌');
    console.log('   Content includes donation details:', htmlContent.includes('donation') ? '✅' : '❌');
    
    return Promise.resolve();
  }

  /**
   * Integration with SendGrid (example)
   */
  public static async sendWithSendGrid(
    to: string,
    subject: string,
    htmlContent: string,
    textContent: string
  ): Promise<void> {
    try {
      const config = this.getConfig();
      const response = await fetch('https://api.sendgrid.com/v3/mail/send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          personalizations: [{
            to: [{ email: to }],
            subject: subject
          }],
          from: { email: config.fromEmail, name: 'Goodbye Malaria' },
          content: [
            {
              type: 'text/plain',
              value: textContent
            },
            {
              type: 'text/html',
              value: htmlContent
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`SendGrid API error: ${response.statusText}`);
      }

      console.log('Email sent successfully via SendGrid');
    } catch (error) {
      console.error('SendGrid email sending failed:', error);
      throw error;
    }
  }

  /**
   * Integration with Mailgun (example)
   */
  public static async sendWithMailgun(
    to: string,
    subject: string,
    htmlContent: string,
    textContent: string
  ): Promise<void> {
    try {
      const config = this.getConfig();
      const domain = process.env.REACT_APP_MAILGUN_DOMAIN || '';
      const apiKey = config.apiKey || process.env.REACT_APP_MAILGUN_API_KEY || '';

      const formData = new FormData();
      formData.append('from', `Goodbye Malaria <${config.fromEmail}>`);
      formData.append('to', to);
      formData.append('subject', subject);
      formData.append('text', textContent);
      formData.append('html', htmlContent);

      const response = await fetch(`https://api.mailgun.net/v3/${domain}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`api:${apiKey}`)}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Mailgun API error: ${response.statusText}`);
      }

      console.log('Email sent successfully via Mailgun');
    } catch (error) {
      console.error('Mailgun email sending failed:', error);
      throw error;
    }
  }

  /**
   * Mock email service for development/testing
   */
  public static async sendMockEmail(
    donationData: DonationFormData,
    transactionId?: string
  ): Promise<void> {
    console.log('=== MOCK EMAIL SERVICE ===');
    console.log('Donation Confirmation Email');
    console.log('To:', donationData.email);
    console.log('Donor:', `${donationData.firstName} ${donationData.lastName}`);
    console.log('Amount:', `R${donationData.amount}`);
    console.log('Type:', donationData.donationType);
    console.log('Transaction ID:', transactionId || 'Generated automatically');
    console.log('Cell Number:', donationData.cellNumber);
    console.log('==========================');
    
    // Simulate email sending delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('✅ Mock email sent successfully!');
  }
}

// Email service validation helper
export const validateEmailConfig = (provider: string): boolean => {
  switch (provider) {
    case 'smtp2go':
      return !!(process.env.REACT_APP_SMTP_USER && process.env.REACT_APP_SMTP_PASS);
    case 'itpac':
      return true; // ITPAC credentials are configured in config file
    case 'sendgrid':
      return !!process.env.REACT_APP_EMAIL_API_KEY;
    case 'mailgun':
      return !!(process.env.REACT_APP_MAILGUN_API_KEY && process.env.REACT_APP_MAILGUN_DOMAIN);
    case 'mock':
      return true;
    default:
      return false;
  }
};
